import LeadBlock from "@bmi-digital/components/lead-block";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { replaceSpaces } from "@bmi-digital/components/utils";
import AnchorLink from "@bmi-digital/components/anchor-link";
import Button from "@bmi-digital/components/button";
import Launch from "@bmi-digital/components/icon/ExternalLink";
import IconList from "@bmi-digital/components/icon-list";
import React from "react";
import RichText from "../../../components/RichText";
import {
  classes,
  StyledBlueCheckIcon,
  StyledDocumentSpan,
  StyledImage,
  StyledImagesContainer,
  StyledLeadBlock,
  StyledLeadBlockCard,
  StyledLeadBlockContent,
  StyledProductDescription
} from "../../../components/styles/ProductLeadBlock.styles";
import { useSiteContext } from "../../../components/Site";
import type { Product } from "../../../types/pim";
import type { TitleWithContentData } from "../../../components/TitleWithContent";

const AboutTabContent = ({
  productBenefits,
  description,
  guaranteesAndWarrantiesImages,
  guaranteesAndWarrantiesLinks,
  awardsAndCertificateDocuments,
  awardsAndCertificateImages,
  sidebarItems
}: {
  productBenefits: Product["productBenefits"];
  description: Product["description"];
  guaranteesAndWarrantiesImages: Product["guaranteesAndWarrantiesImages"];
  guaranteesAndWarrantiesLinks: Product["guaranteesAndWarrantiesLinks"];
  awardsAndCertificateDocuments: Product["awardsAndCertificateDocuments"];
  awardsAndCertificateImages: Product["awardsAndCertificateImages"];
  sidebarItems: readonly TitleWithContentData[];
}) => {
  const { getMicroCopy } = useSiteContext();

  return (
    <StyledLeadBlock>
      <StyledLeadBlockContent>
        <LeadBlock.Content.Section>
          <StyledProductDescription
            component="div"
            dangerouslySetInnerHTML={{ __html: description }}
            data-testid="product-lead-block-description"
          />
        </LeadBlock.Content.Section>
        {guaranteesAndWarrantiesImages.length ||
        guaranteesAndWarrantiesLinks.length ? (
          <LeadBlock.Content.Section>
            <LeadBlock.Content.Heading>
              {getMicroCopy(microCopy.PDP_LEAD_BLOCK_GUARANTEES_WARRANTIES)}
            </LeadBlock.Content.Heading>
            {guaranteesAndWarrantiesImages.length ? (
              <StyledImagesContainer>
                {guaranteesAndWarrantiesImages.map((item, i) => (
                  <StyledImage
                    key={`guarantee-img-${i}`}
                    src={item.url}
                    alt={item.name}
                    data-testid={`guarantee-image-${replaceSpaces(item.name)}`}
                  />
                ))}
              </StyledImagesContainer>
            ) : null}
            {guaranteesAndWarrantiesLinks.map((item, i) => (
              <div key={`link-${i}`}>
                <AnchorLink
                  href={item.url}
                  gtm={{
                    id: "cta-click1",
                    label: item.name,
                    action: item.url
                  }}
                  iconPosition="end"
                  external
                  className={classes["inline-link"]}
                  data-testid={`guarantee-inline-link-${replaceSpaces(
                    item.name
                  )}`}
                >
                  {item.name}
                </AnchorLink>
              </div>
            ))}
          </LeadBlock.Content.Section>
        ) : null}
        {(awardsAndCertificateDocuments.length > 0 ||
          awardsAndCertificateImages.length > 0) && (
          <LeadBlock.Content.Section>
            <LeadBlock.Content.Heading>
              {getMicroCopy(microCopy.PDP_LEAD_BLOCK_AWARDS_CERTIFICATES)}
            </LeadBlock.Content.Heading>
            {awardsAndCertificateImages.length > 0 && (
              <StyledImagesContainer>
                {awardsAndCertificateImages.map((item, i) => (
                  <StyledImage
                    key={`award-img-${i}`}
                    src={item.url}
                    alt={item.name}
                  />
                ))}
              </StyledImagesContainer>
            )}
            {awardsAndCertificateImages.length &&
            awardsAndCertificateDocuments.length ? (
              <br />
            ) : null}
            {awardsAndCertificateDocuments.map((item, i) => (
              <StyledDocumentSpan key={`award-doc-${i}`}>
                <Button
                  variant="outlined"
                  href={item.url}
                  external
                  endIcon={<Launch />}
                  gtm={{
                    id: "cta-click1",
                    label: item.name,
                    action: item.url
                  }}
                >
                  {item.name}
                </Button>
              </StyledDocumentSpan>
            ))}
          </LeadBlock.Content.Section>
        )}
      </StyledLeadBlockContent>
      {(productBenefits?.length || sidebarItems.length) && (
        <StyledLeadBlockCard
          color="blue900"
          data-testid="product-benefits-post-it-card"
        >
          {productBenefits ? (
            <LeadBlock.Card.Section>
              <LeadBlock.Card.Heading>
                {getMicroCopy(microCopy.PDP_LEAD_BLOCK_KEY_FEATURES)}
              </LeadBlock.Card.Heading>
              <LeadBlock.Card.Content>
                <IconList>
                  {productBenefits.map((feature, index) => (
                    <IconList.Item
                      key={`product-benefits-${index}`}
                      icon={<StyledBlueCheckIcon />}
                      title={feature}
                      isCompact
                    />
                  ))}
                </IconList>
              </LeadBlock.Card.Content>
            </LeadBlock.Card.Section>
          ) : null}
          {sidebarItems.length && (
            <LeadBlock.Card.Section>
              <LeadBlock.Card.Heading>
                {sidebarItems[0].title}
              </LeadBlock.Card.Heading>
              <LeadBlock.Card.Content>
                <RichText
                  document={sidebarItems[0].content}
                  theme="secondary"
                  backgroundTheme="dark"
                  gtmLabel={
                    (productBenefits
                      ? `${getMicroCopy(
                          microCopy.PDP_LEAD_BLOCK_KEY_FEATURES
                        )} - `
                      : "") + `${sidebarItems[0].title}`
                  }
                />
              </LeadBlock.Card.Content>
            </LeadBlock.Card.Section>
          )}
        </StyledLeadBlockCard>
      )}
    </StyledLeadBlock>
  );
};

export default AboutTabContent;
